import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate } from '@/lib/constants';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, CheckCircle2, MessageSquare, Send, Upload, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import useFocusItem from '@/hooks/useFocusItem';

export default function QualityPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  useFocusItem(projects);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatProject, setChatProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [projRes, subRes] = await Promise.all([
        api.get('/projects'),
        api.get('/subcontractors?active=true'),
      ]);
      setProjects(projRes.data);
      setSubcontractors(subRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const flagged = projects.filter(p => p.quality_issue);
  const open = flagged.filter(p => !p.quality_resolved);
  const resolved = flagged.filter(p => p.quality_resolved);

  const openChat = async (project) => {
    setChatProject(project);
    setNewMessage('');
    try {
      const [msgRes, fileRes] = await Promise.all([
        api.get(`/projects/${project.id}/quality/messages`),
        api.get(`/files/list?entity_type=quality&entity_id=${project.id}`),
      ]);
      setMessages(msgRes.data);
      setPhotos(fileRes.data);
    } catch { setMessages([]); setPhotos([]); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const { data } = await api.post(`/projects/${chatProject.id}/quality/messages`, { text: newMessage });
      setMessages(prev => [...prev, data]);
      setNewMessage('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const uploadPhoto = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'quality');
        fd.append('entity_id', chatProject.id);
        const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPhotos(prev => [data, ...prev]);
      }
      toast.success('Photo(s) ajoutée(s)');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload error'); }
    finally { setUploading(false); }
  };

  const removePhoto = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setPhotos(prev => prev.filter(f => f.id !== fileId));
    } catch { /* ignore */ }
  };

  const resolveIssue = async (project) => {
    if (!window.confirm(`Marquer le problème qualité de "${project.project_name}" comme RÉSOLU ?`)) return;
    try {
      await api.post(`/projects/${project.id}/quality/resolve`);
      toast.success('Problème qualité résolu');
      setChatProject(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const deleteIssue = async (project) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT le problème qualité de "${project.project_name}" ?\n\nLe score qualité du sous-traitant sera restauré (+10%). Cette action est irréversible.`)) return;
    try {
      await api.delete(`/projects/${project.id}/quality`);
      toast.success('Problème qualité supprimé · score sous-traitant restauré');
      setChatProject(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const subById = (uid) => subcontractors.find(s => s.user_id === uid);

  return (
    <div className="space-y-6" data-testid="quality-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Qualité
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Suivi des problèmes qualité et résolution avec les sous-traitants</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-rose-500/10 rounded-sm flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-400" /></div>
            <div>
              <p className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Cabinet Grotesk' }}>{open.length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Problèmes ouverts</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-sm flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
            <div>
              <p className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Cabinet Grotesk' }}>{resolved.length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Résolus</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-amber-400" /></div>
            <div>
              <p className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Cabinet Grotesk' }}>{subcontractors.filter(s => (s.quality_score ?? 100) < 50).length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">ST en alerte (&lt; 50%)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="issues" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1 h-auto">
          <TabsTrigger value="issues" data-testid="tab-issues" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2">Problèmes ({flagged.length})</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="tab-ranking" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2">Classement qualité ST</TabsTrigger>
        </TabsList>

        <TabsContent value="issues" className="mt-6 space-y-3">
          {loading ? (
            <p className="text-sm text-zinc-500 text-center py-8">Chargement...</p>
          ) : flagged.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-sm">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500/40" />
              Aucun problème qualité en cours. Bon travail !
            </div>
          ) : (
            flagged.map(p => (
              <Card key={p.id} data-focus-id={p.id} className={`bg-zinc-900 border rounded-sm nexus-card ${p.quality_resolved ? 'border-emerald-500/20' : 'border-rose-500/30'}`} data-testid={`quality-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-zinc-100">{p.project_name}</h3>
                        <span className="text-xs text-zinc-500 font-mono">{p.reference}</span>
                        {p.quality_resolved ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Résolu</span>
                        ) : (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose-500/10 text-rose-400 border border-rose-500/20 animate-pulse">Ouvert</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 mb-2">Client : <span className="text-zinc-300">{p.client_name}</span> · ST : <span className="text-amber-400">{p.quality_subcontractor_name || '—'}</span></p>
                      {p.quality_description && <p className="text-sm text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-sm p-2 mb-2">{p.quality_description}</p>}
                      <p className="text-[11px] text-zinc-600">Signalé le {formatDate(p.quality_flagged_at)} par {p.quality_flagged_by}{p.quality_resolved && p.quality_resolved_at ? ` · Résolu le ${formatDate(p.quality_resolved_at)}` : ''}</p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <Button size="sm" onClick={() => openChat(p)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-8" data-testid={`open-quality-chat-${p.id}`}>
                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> Échanger ({(p.quality_messages || []).length})
                      </Button>
                      {!p.quality_resolved && (
                        <Button size="sm" variant="outline" onClick={() => resolveIssue(p)} className="border-emerald-500/30 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500/10 rounded-sm text-xs h-8" data-testid={`resolve-quality-${p.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Marquer résolu
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => deleteIssue(p)} className="border-rose-500/30 bg-rose-500/5 text-rose-400 hover:bg-rose-500/10 rounded-sm text-xs h-8" data-testid={`delete-quality-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="ranking" className="mt-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium p-3">Sous-traitant</th>
                  <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium p-3">Spécialité</th>
                  <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-medium p-3 w-72">Score qualité</th>
                  <th className="text-right text-xs uppercase tracking-wider text-zinc-500 font-medium p-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {subcontractors.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-12 text-zinc-500">Aucun sous-traitant</td></tr>
                ) : [...subcontractors].sort((a, b) => (b.quality_score ?? 100) - (a.quality_score ?? 100)).map(s => {
                  const score = s.quality_score ?? 100;
                  const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-rose-500';
                  const status = score >= 70 ? { label: 'Optimal', cls: 'text-emerald-400' } : score >= 40 ? { label: 'À surveiller', cls: 'text-amber-400' } : score > 0 ? { label: 'Critique', cls: 'text-rose-400' } : { label: 'Liste noire', cls: 'text-rose-500 font-bold' };
                  return (
                    <tr key={s.id} className="border-b border-zinc-800/50" data-testid={`quality-row-${s.id}`}>
                      <td className="p-3 text-sm text-zinc-200 font-medium">{s.company_name}</td>
                      <td className="p-3 text-sm text-zinc-400">{s.specialty || '—'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-zinc-800 rounded-sm overflow-hidden">
                            <div className={`h-full ${color} transition-all`} style={{ width: `${score}%` }} />
                          </div>
                          <span className="text-xs font-mono text-zinc-300 w-10 text-right">{score}%</span>
                        </div>
                      </td>
                      <td className={`p-3 text-sm text-right ${status.cls}`}>{status.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-zinc-600 mt-3">Barème : livraison parfaite +5% · problème qualité signalé −10% · borné 0–100%. En dessous de 40% le sous-traitant est marqué critique, à 0% une révocation est envisageable.</p>
        </TabsContent>
      </Tabs>

      {/* Chat Dialog */}
      <Dialog open={!!chatProject} onOpenChange={() => setChatProject(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quality-chat-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Qualité — {chatProject?.project_name}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {chatProject?.client_name} · ST : <span className="text-amber-400">{chatProject?.quality_subcontractor_name}</span>
              {chatProject?.quality_resolved && <span className="ml-2 text-emerald-400">(Résolu)</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            {chatProject?.quality_description && (
              <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mb-1">Problème signalé</p>
                <p className="text-sm text-zinc-200">{chatProject.quality_description}</p>
              </div>
            )}

            {/* Photos */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400 text-sm">Photos / Pièces jointes</Label>
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Ajouter
                  <input data-testid="quality-photo-input" type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => uploadPhoto(e.target.files)} disabled={uploading} />
                </label>
              </div>
              {photos.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-3 bg-zinc-950 border border-zinc-800 border-dashed rounded-sm">Aucune photo</p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {photos.map(f => <PhotoThumb key={f.id} file={f} onRemove={() => removePhoto(f.id)} />)}
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Échanges ({messages.length})</Label>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="quality-messages-list">
                {messages.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-4">Aucun message. Démarrez la conversation avec le sous-traitant.</p>
                ) : messages.map(m => {
                  const mine = m.sender_role === 'admin';
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-1.5 rounded-sm text-sm border ${mine ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                        <p className="text-[10px] text-zinc-500 mb-0.5">
                          <span className={mine ? 'text-amber-400' : 'text-zinc-400'}>{m.sender_name}</span>
                          <span className="ml-2">{formatDate(m.created_at)}</span>
                        </p>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!chatProject?.quality_resolved && (
                <div className="flex items-center gap-2">
                  <Input data-testid="quality-message-input" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                    placeholder="Écrire un message au sous-traitant..."
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
                  <Button data-testid="quality-send-btn" onClick={sendMessage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            {chatProject && !chatProject.quality_resolved && (
              <Button onClick={() => resolveIssue(chatProject)} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm" data-testid="quality-resolve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Marquer résolu
              </Button>
            )}
            {chatProject && (
              <Button onClick={() => deleteIssue(chatProject)} className="bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 rounded-sm" data-testid="quality-delete-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
              </Button>
            )}
            <Button variant="outline" onClick={() => setChatProject(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoThumb({ file, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/files/download/${file.id}`, { responseType: 'blob' })
      .then(res => { if (!cancelled) setSrc(URL.createObjectURL(res.data)); })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);
  const isImage = (file.content_type || '').startsWith('image/');
  return (
    <div className="relative group bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden aspect-square" data-testid={`quality-photo-${file.id}`}>
      {isImage && src ? (
        <img src={src} alt={file.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2">
          <Upload className="w-6 h-6 text-zinc-500 mb-1" />
          <p className="text-[10px] text-zinc-400 text-center truncate w-full">{file.original_filename}</p>
        </div>
      )}
      <button onClick={onRemove} className="absolute top-1 right-1 bg-zinc-950/80 text-rose-400 hover:text-rose-300 p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}
